import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { FaShoppingCart, FaSearch, FaBars, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { useSession } from '../Cart-Context/session';
import { fetchCartProducts } from '../Cart-Context/context';
import { trackEvent } from '../utils/trackEvents'; 
import '../App.css';
import { API_BASE_URL } from '../utils/apiConfig.js';

const STORE_NAME = "Market";

const Navbar = ({ currentUser, onLogout, currentUserRole }) => {
  const { sessionId, experimentId } = useSession(); // Pull experimentId from hook
  const navigate = useNavigate();
  const location = useLocation();

  // Create a state for the item count so React knows to re-render
  const [itemCount, setItemCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  // state to control search bar visibility based on experiment
  const [showSearch, setShowSearch] = useState(true);
  const [restrictionMode, setRestrictionMode] = useState('HIDE'); 

  // Fetch experiment configuration to check for restrictions
  useEffect(() => {
    const fetchExperimentConfig = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/active-experiment`);
      
        // Only restrict if the user has actually joined this specific experiment
        const isParticipant = res.data && Number(experimentId) === Number(res.data.experimentId);
      
        if (isParticipant) {
          setShowSearch(res.data.showSearch === 1);
          setRestrictionMode(res.data.restrictionMode || 'HIDE');
        } else {
          // If not joined, or study is different, show search bar
          setShowSearch(true);
          setRestrictionMode('HIDE');
        }
      } catch (err) {
        setShowSearch(true);
        setRestrictionMode('HIDE');
      }
    };

  fetchExperimentConfig();
}, [experimentId]); // This triggers when you click 'Join' and the context updates

  useEffect(() => {
    const updateHeight = () => {
        if (navRef.current) {
            document.documentElement.style.setProperty(
                '--navbar-height',
                `${navRef.current.offsetHeight}px`
            );
        }
    };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Define how to calculate the count
  const calculateCount = async () => {
  try {
    const products = await fetchCartProducts();
    
    // Ensure products exists and is an array before reducing
    if (!products || products.length === 0) {
      setItemCount(0);
      return;
    }
    
    const total = products.reduce((acc, item) => acc + (item.quantity || 0), 0);
    setItemCount(total);
  } catch (err) {
    console.error("Navbar: Could not fetch cart count", err);
    setItemCount(0);
  }
};

  // Set up the listener when the component mounts
  useEffect(() => {
    calculateCount(); // Run once on load
    
    window.addEventListener("cartUpdated", calculateCount);
    
    return () => window.removeEventListener("cartUpdated", calculateCount);
  }, []);

  // Changes nav bar based on tab size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const [searchTerm, setSearchTerm] = useState("");

  // This checks the current URL path. It will be true if you're on the login page.
  const isLoginPage = location.pathname === '/login';

  const closeMenu = () => setMenuOpen(false);

  const handleLogout = () => {
    onLogout();

    sessionStorage.clear();

    calculateCount();

    if (location.pathname.startsWith('/research-portal')) {
      navigate('/'); 
    }

    closeMenu();
  };


  // Function for handling the search action
  const handleSearch = () => {
    if (searchTerm.trim()) {
      // Track search interaction from navbar with experimentId
      trackEvent(sessionId, 'SEARCH', 'Navbar Search Bar', { query: searchTerm }, experimentId);
      
      // Navigate to a new page with the query in the URL
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      setSearchTerm("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <>
      <nav className="navbar" ref={navRef}>
        {/* Left Section (Always visible) */}
        <div className="nav-left">
          <Link
            to="/"
            className="store-name" 
            onClick={() => 
              trackEvent(sessionId, 'CLICK', 'Navbar Link', { destination: 'Home'}, experimentId)
            }
            >
            {STORE_NAME}
          </Link> 
        </div>

        {/* Center Section of Navbar */}
        {!isLoginPage && (
          <>
            <div className= "desktop-nav-links">
            <Link
              to="/"
              className="nav-link"
              onClick={() => 
                trackEvent(sessionId, 'CLICK', 'Navbar Link', { destination: 'Home'}, experimentId)
              }
            >
              Home
            </Link>

            <Link 
              to="/products"
              className="nav-link"
              onClick={() => { 
                sessionStorage.removeItem('lastProductsPage');
                trackEvent(sessionId, 'CLICK', 'Navbar Link', { destination: 'Products' }, experimentId);
              }}
            >
              Products
            </Link>
          
            <Link 
              to="/about" 
              className="nav-link"
              onClick={() => 
                trackEvent(sessionId, 'CLICK', 'Navbar Link', { destination: 'About' }, experimentId)
              }
            >
              About
            </Link>
        </div>

        <div className="nav-right desktop-nav-right">
          
          {/* Restrict Search Bar based on Experiment Configuration and Mode */}
          {(showSearch || restrictionMode === 'GREY') && (
            <div className={`search-container ${!showSearch && restrictionMode === 'GREY' ? 'restricted-ghost-mode' : ''}`}>
              <input
                type="text"
                placeholder="Search products..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button className="search-btn" onClick={handleSearch}>
                <FaSearch />
              </button>
            </div>
          )}

          {currentUser ? (
            <div className="dropdown-container">
              <span className="navbar-user-email">{currentUser}</span>
              <div className="dropdown-menu">
                <Link 
                  to="/research-portal" 
                  className="dropdown-link">
                  Research Portal
                </Link>
                <button onClick={handleLogout} className="dropdown-link">
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <button className="log-btn" onClick={() => navigate("/login")}>
              Research Portal
            </button>
          )}

          <button 
            className="cart-btn"
            onClick={() => {
              trackEvent(sessionId, 'CLICK', 'Navbar Cart Button', {}, experimentId);
              navigate("/cart");
            }}
          >
            <div className="cart-icon-wrapper">
              <FaShoppingCart />
              {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
            </div>
          </button>
        </div>

        <button
          className="menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle navigation menu"
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </>
    )}

    {isLoginPage && (
      <div className="nav-right">
        <button className="back-btn" onClick={() => navigate("/")}>
          Back
        </button>
      </div>
      )}
    </nav>

    {!isLoginPage && sessionId && currentUserRole !== 'researcher' && (
      <div className="session-strip">
        <span className="session-label">SessionID:</span> {sessionId}
      </div>
    )}
    {/* new mobile menu based on tab size */}
    {!isLoginPage && menuOpen && (
      <div className="mobile-menu">
        <Link 
          to="/"
          className="mobile-menu-link"
          onClick={() => {
            trackEvent(sessionId, 'CLICK', 'Navbar Link', { destination: 'Home' }, experimentId);
            closeMenu();
          }}
        >
          Home
        </Link>
        
        <Link 
          to="/products"
          className="mobile-menu-link"
          onClick={() => { 
            sessionStorage.removeItem('lastProductsPage');
            trackEvent(sessionId, 'CLICK', 'Navbar Link', { destination: 'Products' }, experimentId);
            closeMenu();
          }}
        >
          Products
        </Link>

        <Link 
          to="/about" 
          className="mobile-menu-link"
          onClick={() => {
            trackEvent(sessionId, 'CLICK', 'Navbar Link', { destination: 'About' }, experimentId);
            closeMenu();
          }}
        >
          About
        </Link>

        {/* Restrict Search Bar in Mobile Menu based on Experiment Configuration and Mode */}
        {(showSearch || restrictionMode === 'GREY') && (
          <div className={`mobile-search ${!showSearch && restrictionMode === 'GREY' ? 'restricted-ghost-mode' : ''}`}>
            <input
              type="text"
              placeholder="Search products..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="search-btn" onClick={handleSearch}>
              <FaSearch />
            </button>
          </div>
        )}

          {currentUser ? (
            <>
              <Link
                to="/research-portal"
                className="mobile-menu-link"
                onClick={closeMenu}
              >
                Research Portal 
              </Link>
              <button className="mobile-menu-link mobile-menu-btn" 
              onClick={handleLogout}
              >
                Logout
              </button>
            </>
          ) : (
            <button 
              className="mobile-menu-link mobile-menu-btn"
              onClick={() => {
                navigate("/login");
                closeMenu();
              }}
            >
              Research Portal
            </button>
          )}

          <button 
            className="mobile-menu-link mobile-menu-btn" 
            onClick={() => {
              trackEvent(sessionId, 'CLICK', 'Navbar Cart Button', {}, experimentId);
              navigate("/cart");
              closeMenu();
            }}
          >
            Cart
          </button>
        </div>
      )}
    </>
  );
};

export default Navbar;